<template>
  <CTabs variant="tabs" class="nav-tabs-boxed">
    <CTab title="Cadastro de filiais">
      <Loading :isActive="loading" />
      <FilialEdit ref="edit" :filiais="filiais" :salvarFilial="salvarFilial" />

      <div v-if="alerta" class="aviso-sincronizacao">
        <fa-icon icon="info-circle" class="aviso-sincronizacao__icone" />
        <span>{{ alerta }}</span>
      </div>

      <div class="listagem__filtros">
        <div class="search-wrapper">
          <fa-icon icon="search" class="search-icon" />
          <input
            type="text"
            class="form-control"
            v-model="termo"
            placeholder="Buscar por código, nome, CNPJ ou cidade..."
          />
        </div>

        <status-filter-bar v-model="status" :chips="chips" :contagens="contagens" />

        <div class="listagem__acoes">
          <button v-on:click="abrirEdicao()" class="button button-success">
            <fa-icon icon="plus" />
            Nova filial
          </button>
        </div>
      </div>

      <EmptyState
        v-if="!loading && filiaisFiltradas.length === 0"
        icon="building"
        title="Nenhuma filial encontrada"
        :description="
          filiais.length === 0
            ? 'As filiais chegam pela integração com o ERP. Rode a carga de filiais ou cadastre uma manualmente.'
            : 'Não há filiais que correspondam à busca ou ao filtro selecionado.'
        "
      />

      <div v-else-if="filiaisFiltradas.length" class="listagem__table-wrapper">
        <table class="listagem__table">
          <thead>
            <tr>
              <th>Filial</th>
              <th>Localização</th>
              <th>Retirada</th>
              <th>Ordem</th>
              <th>Site</th>
              <th>Situação</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="filial in filiaisFiltradas"
              v-bind:key="filial.id"
              class="listagem__row"
              v-on:dblclick="abrirEdicao(filial)"
            >
              <td class="listagem__cell--nome">
                <span class="filial-codigo">{{ filial.id }}</span>
                <span class="filial-nome">
                  <strong>{{ filial.fantasia || filial.razaosocial }}</strong>
                  <small v-if="filial.cnpj">{{ filial.cnpj }}</small>
                </span>
              </td>
              <td>
                <span v-if="filial.cidade || filial.uf">
                  {{ [filial.cidade, filial.uf].filter(Boolean).join(" / ") }}
                </span>
                <span v-else class="texto-vazio">Não informada</span>
              </td>
              <td>
                <div v-if="retiras(filial).length" class="retiradas">
                  <span
                    v-for="retira in retiras(filial)"
                    :key="retira"
                    class="label primary"
                    :title="nomeFilial(retira)"
                  >
                    {{ retira }}
                  </span>
                </div>
                <span v-else class="texto-vazio" :title="'Retira na filial ' + (filial.filialretira_id || filial.id)">
                  Padrão ({{ filial.filialretira_id || filial.id }})
                </span>
              </td>
              <td>{{ filial.ordemexibicao || "-" }}</td>
              <td :inner-html.prop="filial.visivel_site | booleanHtml"></td>
              <td>
                <span class="label" :class="+filial.ativo === 1 ? 'success' : 'warning'">
                  {{ +filial.ativo === 1 ? "Ativa" : "Inativa" }}
                </span>
              </td>
              <td class="listagem__cell--actions">
                <button
                  type="button"
                  class="button button-primary"
                  title="Editar"
                  aria-label="Editar filial"
                  v-on:click="abrirEdicao(filial)"
                >
                  <fa-icon icon="pencil-alt" />
                </button>
                &nbsp;
                <button
                  type="button"
                  class="button button-error"
                  title="Excluir"
                  aria-label="Excluir filial"
                  v-on:click="confirmarExclusao(filial)"
                >
                  <fa-icon icon="trash-alt" />
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </CTab>
  </CTabs>
</template>

<script>
import { get, post, put, del } from "../../helpers/apiRequest";
import EmptyState from "../../components/EmptyState";
import StatusFilterBar from "../../components/StatusFilterBar";
import FilialEdit from "./FilialEdit";

const CHIPS = [
  { chave: "todas", label: "Todas" },
  { chave: "ativas", label: "Ativas", dot: true, cor: "#2e7d32" },
  { chave: "inativas", label: "Inativas", dot: true, cor: "#b54708" },
  { chave: "retirada", label: "Com retirada múltipla", dot: true, cor: "#3a3e81" },
];

export default {
  name: "filiais",
  components: {
    EmptyState,
    StatusFilterBar,
    FilialEdit,
  },
  data() {
    return {
      filiais: [],
      termo: "",
      status: "todas",
      chips: CHIPS,
      alerta: "",
      loading: false,
    };
  },
  computed: {
    contagens() {
      return {
        todas: this.filiais.length,
        ativas: this.filiais.filter((filial) => +filial.ativo === 1).length,
        inativas: this.filiais.filter((filial) => +filial.ativo !== 1).length,
        retirada: this.filiais.filter((filial) => this.retiras(filial).length).length,
      };
    },
    filiaisFiltradas() {
      const termo = this.termo.trim().toLowerCase();

      return this.filiais.filter((filial) => {
        const casaTermo =
          !termo ||
          [filial.id, filial.fantasia, filial.razaosocial, filial.cnpj, filial.cidade]
            .join(" ")
            .toLowerCase()
            .indexOf(termo) >= 0;

        const casaStatus =
          this.status === "todas" ||
          (this.status === "ativas" && +filial.ativo === 1) ||
          (this.status === "inativas" && +filial.ativo !== 1) ||
          (this.status === "retirada" && this.retiras(filial).length > 0);

        return casaTermo && casaStatus;
      });
    },
  },
  methods: {
    carregar() {
      this.loading = true;
      return get("/admin/filiais")
        .then((json) => {
          this.filiais = json.itens || json || [];
        })
        .catch((error) => {
          this.$swal({
            title: "Erro ao buscar filiais",
            text: error.message || "Não foi possível carregar a listagem.",
            icon: "error",
            confirmButtonText: "Fechar",
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    carregarAlerta() {
      get("/admin/filiais/check-alertas")
        .then((json) => {
          this.alerta = json.message || "";
        })
        .catch(() => {
          this.alerta = "";
        });
    },
    retiras(filial) {
      return String(filial.filiaisretiras_ids || "")
        .split(",")
        .map((id) => id.trim())
        .filter((id) => id !== "");
    },
    nomeFilial(id) {
      const filial = this.filiais.find((item) => String(item.id) === String(id));
      return filial ? `${filial.id} - ${filial.fantasia || filial.razaosocial}` : id;
    },
    abrirEdicao(filial = null) {
      this.$refs.edit.abrir(filial);
    },
    async salvarFilial(criando, dados, id) {
      try {
        if (criando) {
          await post("/admin/filiais", dados);
        } else {
          await put(`/admin/filiais/${encodeURIComponent(id)}`, dados);
        }

        this.$swal({
          title: criando ? "Filial cadastrada" : "Filial alterada",
          icon: "success",
          timer: 1500,
          showConfirmButton: false,
        });
        await this.carregar();
      } catch (error) {
        this.$swal({
          title: "Não foi possível salvar a filial",
          text: error.message,
          icon: "error",
          confirmButtonText: "Fechar",
        });
        throw error;
      }
    },
    confirmarExclusao(filial) {
      this.$swal({
        title: `Excluir a filial ${filial.id}?`,
        text: "Essa ação não poderá ser desfeita. Se a filial vier do ERP, ela volta na próxima sincronização.",
        showCancelButton: true,
        icon: "warning",
        confirmButtonText: "Excluir",
        cancelButtonText: "Cancelar",
      }).then((resposta) => {
        if (!resposta.value) {
          return;
        }

        del(`/admin/filiais/${encodeURIComponent(filial.id)}`)
          .then(() => this.carregar())
          .catch((error) => {
            this.$swal({
              title: "Não foi possível excluir a filial",
              text: error.message,
              icon: "error",
              confirmButtonText: "Fechar",
            });
          });
      });
    },
  },
  beforeMount() {
    this.carregar();
    this.carregarAlerta();
  },
};
</script>

<style scoped>
.aviso-sincronizacao {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 14px;
  margin-bottom: 16px;
  border: 1px solid #d6e4ff;
  border-radius: 8px;
  background: #f5f8ff;
  font-size: 13px;
  color: #344054;
}
.aviso-sincronizacao__icone {
  color: #3a5bd9;
  margin-top: 3px;
  flex-shrink: 0;
}
.search-wrapper {
  position: relative;
  width: 300px;
}
.search-wrapper .form-control {
  height: 38px;
  padding-right: 35px;
}
.search-icon {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  color: #00416f;
}
.listagem__filtros {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}
.listagem__acoes {
  margin-left: auto;
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}
.button {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: none;
  border-radius: 4px;
  font-weight: 500;
  transition: all 0.2s ease;
  cursor: pointer;
}
.listagem__acoes .button {
  height: 38px;
  padding: 0 20px;
  white-space: nowrap;
  font-size: 15px;
  justify-content: center;
}
@media (max-width: 991px) {
  .search-wrapper {
    width: 100%;
  }
  .listagem__acoes {
    margin-left: 0;
    width: 100%;
  }
  .listagem__acoes .button {
    flex: 1;
  }
}
.listagem__table-wrapper {
  border: 1px solid #e4e7ea;
  border-radius: 10px;
  overflow-x: auto;
}
.listagem__table {
  width: 100%;
  min-width: 860px;
  border-collapse: collapse;
  font-size: 13px;
}
.listagem__table thead tr {
  background: #f9fafb;
  border-bottom: 1px solid #e4e7ea;
}
.listagem__table thead th {
  padding: 10px 14px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  color: #667085;
  letter-spacing: 0.4px;
  white-space: nowrap;
}
.listagem__table tbody td {
  padding: 12px 14px;
  border-bottom: 1px solid #f0f0f0;
  vertical-align: middle;
  color: #344054;
}
.listagem__row:last-child td {
  border-bottom: none;
}
.listagem__row:hover td {
  background: #f9fafb;
}
.listagem__cell--nome {
  display: flex;
  align-items: center;
  gap: 12px;
}
.filial-codigo {
  min-width: 36px;
  height: 36px;
  padding: 0 8px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #eef0f8;
  color: #3a3e81;
  font-weight: 700;
  font-size: 13px;
}
.filial-nome {
  display: flex;
  flex-direction: column;
}
.filial-nome strong {
  color: #101828;
  font-weight: 600;
}
.filial-nome small {
  color: #667085;
}
.retiradas {
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
}
.texto-vazio {
  color: #98a2b3;
}
.listagem__cell--actions {
  text-align: right;
  white-space: nowrap;
  width: 110px;
}
</style>
